<template>
  <div class="notification-update-card">
    <p class="notification-update-card__title">{{ title }}</p>

    <template v-if="isRowBased">
      <div v-for="(row, index) in visibleRows" :key="index" class="notification-update-card__update">
        <BaseBadge
          v-if="row.kind"
          class="notification-update-card__badge"
          :class="{ 'is-removed': isRemoved(row) }"
          :icon="badgeIcon(row)"
        />
        <span v-else class="notification-update-card__badge-spacer"></span>
        <div class="notification-update-card__content">
          <p
            class="notification-update-card__label"
            :class="{ 'is-unread': unread && row.kind, 'is-removed': isRemoved(row), 'is-unchanged': !row.kind }"
          >
            {{ row.label }}
          </p>
          <div v-if="row.amount !== null" class="notification-update-card__amount">
            <span>{{ formatNumToCurrency(row.amount, 0) }}</span>
            <BaseTag v-if="isLowestBid(row)" tone="success" preset="soft">
              {{ $t('notifications-center.lowest') }}
            </BaseTag>
          </div>
        </div>
      </div>
      <p v-if="hiddenRowsCount > 0" class="notification-update-card__others">
        {{ $t('notifications-center.and-others', { count: hiddenRowsCount }) }}
      </p>
    </template>

    <div v-else class="notification-update-card__values">
      <span v-if="oldValue" class="notification-update-card__old-value">{{ oldValue }}</span>
      <BaseIcon v-if="oldValue && newValue" name="arrow-right" class="notification-update-card__arrow" />
      <span v-if="newValue" class="notification-update-card__new-value" :class="{ 'is-unread': unread }">
        {{ newValue }}
      </span>
    </div>
  </div>
</template>

<script>
import BaseBadge from '@/components/base/BaseBadge.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BaseTag from '@/components/base/BaseTag.vue';
import { formatNumToCurrency } from '@/util/currency-util';
import {
  CHANGE_KINDS,
  getChangeRows,
  getChangeTitle,
  getLowestBidAmount,
  getScalarValues,
  isRowChange,
} from '@/util/notification-changes-util';

const MAX_VISIBLE_ROWS = 3;

export default {
  name: 'NotificationUpdateCard',
  components: {
    BaseBadge,
    BaseIcon,
    BaseTag,
  },
  props: {
    tenderChange: {
      type: Object,
      required: true,
    },
    unread: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isRowBased() {
      return isRowChange(this.tenderChange);
    },
    title() {
      return getChangeTitle(this.tenderChange);
    },
    rows() {
      return this.isRowBased ? getChangeRows(this.tenderChange) : [];
    },
    visibleRows() {
      return this.rows.slice(0, MAX_VISIBLE_ROWS);
    },
    hiddenRowsCount() {
      return Math.max(0, this.rows.length - MAX_VISIBLE_ROWS);
    },
    lowestBidAmount() {
      return this.tenderChange.field === 'bids' ? getLowestBidAmount(this.rows) : null;
    },
    scalarValues() {
      return this.isRowBased ? {} : getScalarValues(this.tenderChange);
    },
    oldValue() {
      return this.scalarValues.oldValue;
    },
    newValue() {
      return this.scalarValues.newValue;
    },
  },
  methods: {
    formatNumToCurrency,
    isRemoved(row) {
      return row.kind === CHANGE_KINDS.REMOVED;
    },
    badgeIcon(row) {
      if (this.isRemoved(row)) return 'minus';
      return this.tenderChange.field === 'awards' ? 'award' : 'plus';
    },
    isLowestBid(row) {
      return this.lowestBidAmount !== null && !this.isRemoved(row) && row.amount === this.lowestBidAmount;
    },
  },
};
</script>

<style lang="scss" scoped>
.notification-update-card {
  display: flex;
  flex-direction: column;
  gap: $space-1;
  width: 100%;
  padding: $space-sm 10px;
  border-radius: $radius-md;
  background: $color-surface-sunken;

  &__title {
    margin: 0;
    color: $color-text-body;
    font-size: 10px;
    font-weight: $font-weight-bold;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }

  &__update {
    display: flex;
    align-items: center;
    gap: $space-sm;
    width: 100%;
  }

  &__badge {
    flex-shrink: 0;
    background: $color-semantic-success;

    &.is-removed {
      background: $color-semantic-error;
    }
  }

  &__badge-spacer {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: $space-1;
    flex: 1;
    min-width: 0;
  }

  &__label {
    margin: 0;
    color: $color-accent-500;
    font-size: 12.5px;

    &.is-unread {
      font-weight: $font-weight-bold;
    }

    &.is-removed {
      text-decoration: line-through;
    }

    &.is-unchanged {
      color: $color-text-body;
    }
  }

  &__amount {
    display: flex;
    align-items: center;
    gap: $space-sm;
    color: $color-text-body;
    font-size: $font-size-xsmall;
  }

  &__others {
    margin: 0;
    color: $color-text-body;
    font-size: 12.5px;
  }

  &__values {
    display: flex;
    align-items: center;
    gap: $space-sm;
    font-size: 12.5px;
    color: $color-text-body;
  }

  &__old-value {
    text-decoration: line-through;
    color: $color-text-muted;
  }

  &__arrow {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    color: $color-text-muted;
  }

  &__new-value {
    color: $color-text-title;

    &.is-unread {
      font-weight: $font-weight-bold;
    }
  }
}
</style>
